import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import css from './PrivacyPolicy.css';

const PrivacyPolicy = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <p>
        Sydney Data Services Pty Ltd t/as Kindershare (
        <span className="highlight">Kindershare, we, our </span> or <span>us</span>) is committed to
        protecting the privacy of your personal information. This policy explains how we collect,
        use, share hold and retain your personal information in accordance with the Privacy Act 1988
        (Australia).
      </p>

      <p>
        We may update this policy from time to time by posting an updated version on our website.
      </p>

      <h2>The types of personal information we collect:</h2>
      <p>
        To provide you with services, or to perform a function related to providing you with
        services, we need to collect and hold some personal information about you. The types of
        information we may collect include:
      </p>
      <ul>
        <li>
          <span>
            your contact details (such as your name, address, email address and contact phone
            numbers); and
          </span>
        </li>
        <li>
          <span>
            records of your use of your the services and your communications with us and other users
            of the services.&nbsp;
          </span>
        </li>
      </ul>
      <p>
        You acknowledge that our third-party payment provider, Stripe, will also collect other
        information about you, including banking details.
      </p>

      <p>
        If you don’t provide us with the personal information we need, we may not be able to provide
        you with the service you want or we may be restricted in the way that service can be
        supplied.
      </p>

      <h2>How your personal information is collected and held</h2>
      <p>
        We usually collect personal information about you when you use our service or interact
        directly with us. For example, we collect your personal details when you sign up to, or
        perform transactions, on our website. We also collect information when you engage with us by
        phone or email. We may also collect personal information from you in connection with events,
        promotions or marketing activities conduct by, or on behalf of, Kindershare.
      </p>

      <p>We may also collect personal information about you indirectly, including from:</p>

      <ul>
        <li>
          <p>
            <span>anyone authorised to act on your behalf;</span>
          </p>
        </li>
        <li>
          <p>
            <span>our contractors and suppliers;</span>
          </p>
        </li>
        <li>
          <p>
            <span>public sources.</span>
          </p>
        </li>
      </ul>

      <h2>How we use your personal information</h2>
      <p>
        We use your personal information to provide you with services and support. We may also use
        your personal information for related purposes if reasonably required in the normal course
        of our business, such as:
      </p>

      <ul>
        <li>
          <p>
            <span>dealing with your enquiries and providing you with customer support;</span>
          </p>
        </li>
        <li>
          <p>
            <span>
              managing your services, including account management, billing, processing payments and
              collecting payments;
            </span>
          </p>
        </li>
        <li>
          <p>
            <span>investigating complaints and carrying out dispute resolution;</span>
          </p>
        </li>
        <li>
          <p>
            <span>administering our agreement with you;</span>
          </p>
        </li>
        <li>
          <p>
            <span>improving our services to you and the functionality of our website;</span>
          </p>
        </li>
        <li>
          <div>
            <span>research and development, business planning and staff training; and</span>
            <br />
          </div>
        </li>
        <li>
          <p>
            <span>
              communicating with you about the services and special promotions we offer which we
              think may be of interest to you, if you have opted in to receive such communications.
            </span>
          </p>
        </li>
      </ul>
      <p>
        You consent to us using your personal information to promote our service and make special
        promotions and other related offers to you, including via email. We may continue to use this
        information for such activities until you indicate otherwise. You may opt out of receiving
        marketing email by using the unsubscribe feature or by emailing us at{' '}
        <a href="mailto:privacy@kindershare.com">privacy@kindershare.com</a>
      </p>

      <h2>How we share your personal information</h2>

      <p>
        We may need to share your personal information with other organisations in connection with
        our supply of services to you, such as our third-party payment provider. We will however,
        not share your personal information, without your consent, outside of our organisation to
        enable those other organisations to market products or services to you.
      </p>

      <p>We may disclose your personal information to:</p>

      <ul>
        <li>
          <p>
            <span>our suppliers and agents so we can supply the service to you;</span>
          </p>
        </li>
        <li>
          <p>
            <span>
              our service providers who perform functions and services on our behalf, such as our
              payment platform provider, contact centre services or IT services;
            </span>
          </p>
        </li>
        <li>
          <p>
            <span>our legal, accounting and financial advisers;</span>
          </p>
        </li>
        <li>
          <p>
            <span>any person, with your consent;</span>
          </p>
        </li>
        <li>
          <p>
            <span>
              government, law enforcement and regulatory bodies where this is required for us to
              comply with our legal obligations; or
            </span>
          </p>
        </li>
        <li>
          <p>
            <span>persons in relation to a purchase or potential sale of our business.</span>
          </p>
        </li>
      </ul>

      <h2>How we retain your personal information</h2>

      <p>
        We will keep your personal information only as long as needed to satisfy the purposes for
        which it was collected or as required or permitted by law.
      </p>

      <h2>How we protect your personal information</h2>

      <p>
        We treat your personal information as confidential. We store your personal information
        electronically and/or in hard copy form. We take a range of measures to protect the security
        of your personal information, including by storing electronic information on secured servers
        that are hosted in secure data centre facilities. We do not store bank account or credit
        card details on our systems.
      </p>

      <h2>Our website</h2>

      <p>
        We may collect information about visits to our websites for marketing and statistical
        purposes to improve the way we interact with you. We use third party cookies, including
        Google Analytics and DoubleClick cookies, to gauge how our website is used and gain insights
        from any demographic and interests data that may be available. You can usually remove or
        block cookies (by using the settings in your browser), but it may affect your ability to use
        our website.
      </p>

      <p>
        When you are logged into our website, we will log transactions you perform and related
        information.
      </p>

      <h2>How to access and correct your personal information</h2>

      <p>
        Your personal information can be accessed through the website once you have logged in, or by
        contacting us at <a href="mailto:privacy@kindershare.com">privacy@kindershare.com</a>. You
        can access and correct most of your personal information by logging into our website, or by
        contacting us at <a href="mailto:privacy@kindershare.com">privacy@kindershare.com</a> .
      </p>

      <p>
        If you have an enquiry regarding privacy, or you wish to raise a complaint, you can contact
        the Privacy Officer by email at{' '}
        <a href="mailto:privacy@kindershare.com">privacy@kindershare.com</a>.
      </p>

      <p>(updated 26 October 2023)</p>
    </div>
  );
};

PrivacyPolicy.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

PrivacyPolicy.propTypes = {
  rootClassName: string,
  className: string,
};

export default PrivacyPolicy;
